import { filter, find, isEmpty, map, sumBy, uniq, values } from "lodash";
import React, { useCallback, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useContacts } from "../../../context/contacts";
import { useInstalls } from "../../../context/installs";
import { getContactInstallCommissions } from "../../../context/installs/utils";
import useCurrency, { CurrencyKey } from "../../../utils/useCurrency";
import { useHntMarket } from "../../../utils/useHntMarket";
import ContactPaymentsDataTable from "../../Workspace/common/ContactPaymentsDataTable";
import { PaginationKey } from "../../Workspace/common/usePagination";

const ContactPaymentsTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { contacts, currentContact } = useContacts();
  const { installs } = useInstalls();
  const { getCurrencyTitle, currencyDisplay } = useCurrency();
  const { displayInCurrency } = useHntMarket();

  const contact = useMemo(
    () =>
      currentContact ||
      (props.match.params.id ? contacts[props.match.params.id] : undefined),
    [contacts, currentContact, props.match.params.id]
  );

  const contactPayments = useMemo(
    () => contact?.contact_payments || [],
    [contact?.contact_payments]
  );

  const contactCurrencies = useMemo(() => {
    const contactInstalls = values(installs).filter(
      ({ commissions }) =>
        !!find(commissions, { contact_uuid: props.match.params.id })
    );
    const contactCommissions = map(
      getContactInstallCommissions(props.match.params.id, contactInstalls),
      "commission"
    );

    return uniq(
      map(contactCommissions, "currency").concat(
        map(contactPayments, "currency")
      )
    );
  }, [contactPayments, installs, props.match.params.id]);

  const getPaidAmountByCurrency = useCallback(
    (currency: CurrencyKey) =>
      sumBy(filter(contactPayments, { currency }), "amount"),
    [contactPayments]
  );

  return (
    <>
      {!isEmpty(contactCurrencies) && (
        <div className="sm:my-4 sm:mx-4 my-4 mx-4">
          <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {contactCurrencies.map((currency) => {
              const paidAmount = getPaidAmountByCurrency(currency);
              return (
                <div
                  className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
                  key={currency}
                >
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                      Payments in {getCurrencyTitle(currency)}
                    </dt>

                    <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                      {currency === CurrencyKey.hnt
                        ? displayInCurrency(paidAmount, {
                            withHntSuffix: true,
                            needsDivision: true
                          })
                        : currencyDisplay(currency, paidAmount)}
                    </dd>
                  </div>
                </div>
              );
            })}
          </dl>
        </div>
      )}

      <div className="pt-4 -mb-6 h-96">
        <ContactPaymentsDataTable
          paginationKey={PaginationKey.contactPayments}
          title="Contact payments"
          contactPayments={contactPayments}
          loading={!contact}
          contactUuid={props.match.params.id}
        />
      </div>
    </>
  );
};

export default ContactPaymentsTab;
